import { Schema } from "@effect/schema";
import { Config, Effect, pipe, Redacted } from "effect";
import { ConcurrentRateLimiterService } from "../concurrent-rate-limiter";
import { HttpService } from "../types";
import { CmcClientError, CmcStatus } from "./types";
import { match, P } from "ts-pattern";
import { ClientError, RateLimitError, ServerError } from "@silo/types";

const ORIGIN = "https://pro-api.coinmarketcap.com";
type Endpoint = `/${string}`;

export type Request = ReturnType<typeof request>;
export type RequestSuccess = Effect.Effect.Success<Request>;
export type RequestError = Effect.Effect.Error<Request>;
export type RequestContext = Effect.Effect.Context<Request>;

export function request<A, I, R>(
  endpoint: Endpoint,
  schema: Schema.Schema<A, I, R>
) {
  return Effect.gen(function* () {
    const concurrentRateLimiter = yield* ConcurrentRateLimiterService;
    const http = yield* HttpService;
    const COIN_MARKET_CAP_KEY = yield* Config.redacted("COIN_MARKET_CAP_KEY");

    const response = yield* Effect.tryPromise({
      try: () =>
        concurrentRateLimiter.queuePromise(() =>
          http.fetch(`${ORIGIN}${endpoint}`, {
            headers: {
              "X-CMC_PRO_API_KEY": Redacted.value(COIN_MARKET_CAP_KEY),
            },
          })
        ),
      catch: (cause) => new CmcClientError({ cause }),
    });

    const json = yield* Effect.tryPromise({
      try: () => response.json(),
      catch: (cause) => new CmcClientError({ cause }),
    });

    const result = yield* pipe(json, Schema.decodeUnknown(CmcStatus));

    const { status } = response;
    const {
      status: {
        error_code: errorCode,
        error_message: errorMessage,
        credit_count: credits,
      },
    } = result;

    yield* match(status)
      .returnType<
        Effect.Effect<void, ServerError | RateLimitError | ClientError, never>
      >()
      .with(P.number.gte(500), () =>
        Effect.fail(
          new ServerError({
            status,
            code: errorCode,
            message: errorMessage ?? "",
          })
        )
      )
      .with(429, () =>
        Effect.fail(
          new RateLimitError({
            status,
            code: errorCode,
            message: errorMessage ?? "",
          })
        )
      )
      .with(P.number.gte(400), () =>
        Effect.fail(
          new ClientError({
            status,
            code: errorCode,
            message: errorMessage ?? "",
          })
        )
      )
      .otherwise(() => Effect.void);

    const data = yield* pipe(result.data, Schema.decodeUnknown(schema));

    return { data, status, credits };
  });
}
